import { string } from 'fp-ts';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { LocalizedDate, LocalizedText, useFeatureFlag, useTimezone } from '@mews/framework';
import {
    CardPaymentMethodDetails,
    PaymentMethod,
    PaymentMethodDetailResult,
    SepaPaymentMethodDetails,
} from '@mews-commander/data-access';
import { WithAccountId } from '@mews-commander/types';
import { Stack, Typography } from '@optimus-web/core';
import { Dash, FlagIcon, LocalizedDateTimeFromUtc, LocalizedLabel, MultilineText } from '@mews-commander/ui';
import { parseExpirationDate } from '@mews-commander/legacy-entities/src/ducks/paymentCards';
import { PaymentMethodImageType } from '@mews/business-components';
import PaymentCardIcon from '../../../../../components/commander-ui/PaymentCardIcon';
import { PaymentMethodStateTag } from '../../common/PaymentMethodStateTag';
import { PaymentMethodIcon } from '../../../../../components/commander-ui/PaymentMethodIcon';
import { PaymentPlanDetailsLink } from '../../views/PaymentPlanDetails/PaymentPlanDetailsLink';
import { ReservationReferenceLinks } from './ReservationReferenceLinks';
import { CountryName } from './CountryName';

export const useGetPaymentMethodEntries = ({
    paymentMethod,
    accountId,
}: { paymentMethod: PaymentMethodDetailResult } & WithAccountId) => {
    const getSepaPaymentMethodEntries = useGetSepaPaymentMethodEntries(accountId);
    const getCardPaymentMethodEntries = useGetCardPaymentMethodEntries(accountId);

    if (paymentMethod.sepa) {
        return getSepaPaymentMethodEntries(paymentMethod.sepa);
    }

    if (paymentMethod.paymentCard) {
        return getCardPaymentMethodEntries(paymentMethod.paymentCard);
    }

    return null;
};

const useGetCardPaymentMethodEntries = (accountId: string) => {
    const isPaymentPlanEnabled = useFeatureFlag(FeatureFlag.paymentPlanEnabled);
    const timezone = useTimezone();

    return (paymentCard: CardPaymentMethodDetails) => {
        const {
            type,
            format,
            lastDigits,
            holderName,
            expiration,
            state,
            issuingBank,
            issuingCountryCode,
            storageId,
            source,
        } = paymentCard;

        return [
            {
                textKey: TextKey.PaymentMethod,
                value: (
                    <Stack spacing="100" itemAlignment="center">
                        <PaymentCardIcon type={type} width={24} />
                        <Typography textStyle="bodyMediumStrong" as="div">
                            {type}
                        </Typography>
                    </Stack>
                ),
            },
            {
                textKey: TextKey.Type,
                value: (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {format}
                    </Typography>
                ),
            },
            {
                textKey: TextKey.Number,
                value: lastDigits ? (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {lastDigits}
                    </Typography>
                ) : (
                    <Dash />
                ),
            },
            {
                textKey: TextKey.Owner,
                value: holderName ? (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {holderName}
                    </Typography>
                ) : (
                    <Dash />
                ),
            },
            {
                textKey: TextKey.ExpiryDate,
                value: expiration ? <LocalizedDate date={parseExpirationDate(expiration, timezone)} /> : <Dash />,
            },
            {
                textKey: TextKey.Status,
                value: <PaymentMethodStateTag paymentMethod={PaymentMethod.PaymentCard} state={state} />,
            },
            {
                textKey: TextKey.IssuerName,
                value: issuingBank ? (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {issuingBank}
                    </Typography>
                ) : (
                    <Dash />
                ),
            },
            {
                textKey: TextKey.IssuerCountry,
                value: getPaymentMethodCountryEntryValue(issuingCountryCode),
            },
            ...(isPaymentPlanEnabled ? paymentPlanEntries(paymentCard, accountId) : []),
            {
                textKey: TextKey.Token,
                value: storageId ? (
                    <Typography textStyle="bodyMediumStrong" wordBreak="break-all" as="div">
                        {storageId}
                    </Typography>
                ) : (
                    <Dash />
                ),
            },
            {
                textKey: TextKey.Created,
                value: getCreatedEntryValue(paymentCard),
            },
            {
                textKey: TextKey.Source,
                value: source ? (
                    <Typography textStyle="bodyMediumStrong" align="right" as="div">
                        <MultilineText text={source} />
                    </Typography>
                ) : (
                    <Dash />
                ),
            },
        ];
    };
};

const useGetSepaPaymentMethodEntries = (accountId: string) => {
    const isPaymentPlanEnabled = useFeatureFlag(FeatureFlag.paymentPlanEnabled);

    return (sepa: SepaPaymentMethodDetails) => {
        const { payerName, iban, state, countryCode, source, reservationGroupName, mandateReference } = sepa;

        return [
            {
                textKey: TextKey.PaymentMethod,
                value: (
                    <Stack spacing="100" itemAlignment="center">
                        <PaymentMethodIcon type={PaymentMethodImageType.SepaDirectDebit} small />
                        <LocalizedText textKey={TextKey.Sepa} upperCase />
                    </Stack>
                ),
            },
            {
                textKey: TextKey.Type,
                value: <LocalizedLabel textKey={TextKey.PaymentMethodDirectDebit} as="div" />,
            },
            {
                textKey: TextKey.Owner,
                value: (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {payerName}
                    </Typography>
                ),
            },
            {
                textKey: TextKey.Iban,
                value: (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {iban}
                    </Typography>
                ),
            },
            {
                textKey: TextKey.Status,
                value: <PaymentMethodStateTag paymentMethod={PaymentMethod.SepaDirectDebit} state={state} />,
            },
            {
                textKey: TextKey.IssuerCountry,
                value: getPaymentMethodCountryEntryValue(countryCode),
            },
            ...(isPaymentPlanEnabled
                ? paymentPlanEntries(sepa, accountId)
                : [
                      {
                          textKey: TextKey.PaymentMethodDetailsReservation,
                          value: reservationGroupName ? (
                              <Typography textStyle="bodyMediumStrong" as="div">
                                  {reservationGroupName}
                              </Typography>
                          ) : (
                              <Dash />
                          ),
                      },
                  ]),
            {
                textKey: TextKey.PaymentMethodDetailsMandateReference,
                value: mandateReference ? (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {mandateReference}
                    </Typography>
                ) : (
                    <Dash />
                ),
            },
            {
                textKey: TextKey.LatestCapture,
                value: getCreatedEntryValue(sepa),
            },
            {
                textKey: TextKey.LatestSource,
                value: (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {source}
                    </Typography>
                ),
            },
        ];
    };
};

const paymentPlanEntries = (
    {
        reservationReferenceIdentifiers,
        paymentPlanName,
        paymentPlanId,
    }: SepaPaymentMethodDetails | CardPaymentMethodDetails,
    accountId: string
) => [
    {
        textKey: TextKey.PaymentMethodDetailsReservationNumber,
        value:
            reservationReferenceIdentifiers && reservationReferenceIdentifiers.length > 0 ? (
                <ReservationReferenceLinks reservations={reservationReferenceIdentifiers} />
            ) : (
                <Dash />
            ),
    },
    {
        textKey: TextKey.PaymentMethodDetailsPaymentPlan,
        value:
            paymentPlanName && paymentPlanId ? (
                <PaymentPlanDetailsLink
                    linkContent={paymentPlanName}
                    paymentPlanId={paymentPlanId}
                    accountId={accountId}
                />
            ) : (
                <Dash />
            ),
    },
];

const getPaymentMethodCountryEntryValue = (countryCode?: string | null) =>
    countryCode ? (
        <Stack itemAlignment="center" spacing="100">
            <FlagIcon small country={string.toLowerCase(countryCode)} />
            <Typography textStyle="bodyMediumStrong" as="div">
                <CountryName countryCode={countryCode} />
            </Typography>
        </Stack>
    ) : (
        <Dash />
    );

const getCreatedEntryValue = ({
    createdUtc,
    creatorProfileName,
}: SepaPaymentMethodDetails | CardPaymentMethodDetails) => (
    <Stack vertical itemAlignment="end">
        <Typography textStyle="bodyMediumStrong" align="right" as="div">
            <LocalizedDateTimeFromUtc utcString={createdUtc} />
        </Typography>
        <Typography textStyle="bodyMediumStrong" as="div">
            {creatorProfileName}
        </Typography>
    </Stack>
);
